import {
  Component,
  EventEmitter,
  Inject, Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Account } from 'src/app/modules/account/models/account';
import { DocumentSide, DocumentType, IdentityType, KycDocument } from 'src/app/modules/account/models/document';
import { AccountStoreService } from 'src/app/modules/account/store/account.store.service';
import { Guid } from '@core/utils/guid';
import { isPlatformBrowser } from '@angular/common';
import { TRANSLATION_SCOPE } from '@modules/instructor/i18n/scope';

@Component({
  selector: 'identity-verification-form',
  templateUrl: './identity-verification-form.component.html',
  styleUrls: ['./identity-verification-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class IdentityVerificationFormComponent implements OnInit, OnDestroy {
  @Input() public isTabReadOnly: boolean = false;

  @Output() OnBack = new EventEmitter<string>();
  @Output() OnSubmit = new EventEmitter<string>();
  @Output() OnApprove = new EventEmitter<string>();
  @Output() OnReject = new EventEmitter<string>();
  @Output() OnPending = new EventEmitter<string>();
  @Output() OnComplete = new EventEmitter<string>();

  identityTypes = [
    {
      value: 'PASSPORT',
      label: 'Pass',
    },
    {
      value: 'DRIVING_LICENSE',
      label: 'Førerkort - fremside og bakside er påkrevd',
    },
    {
      value: 'ID_CARD',
      label: 'ID-kort',
    },
    {
      value: 'RESIDENT_PERMIT_ID',
      label: 'Oppholdskort',
    },
  ];
  translationScope = TRANSLATION_SCOPE;
  selectedIdentityType: string;

  idDocumentFront: File = null;
  idDocumentBack: File = null;
  addressDocumentFront: File = null;
  addressDocumentBack: File = null;
  addressPdfDocumentFront: any = null;
  localKycDocument: KycDocument[] = [];

  account: Account = null;
  kycDocuments: KycDocument[] = [];
  documentRef: string;

  // Internal property
  isUpdate = false;
  destroy$: Subject<any>;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private accountStoreService: AccountStoreService
  ) {
    this.destroy$ = new Subject();
  }

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.documentRef = Guid.NewGuid();

    this.accountStoreService
      .getBusinessAccount()
      .pipe(takeUntil(this.destroy$))
      .subscribe((account: Account) => {
        this.account = account;
      });

    this.accountStoreService
      .getKycDocument()
      .pipe(takeUntil(this.destroy$))
      .subscribe((kycDocuments: KycDocument[]) => {
        this.kycDocuments = kycDocuments;
        if (this.localKycDocument.length > kycDocuments.length) {
          this.uploadDocument(this.localKycDocument[this.kycDocuments.length]);
        } else {
          this.localKycDocument = [];
        }
        if (kycDocuments.length) {
          this.isUpdate = true;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDocumentTypeSelect(identityType: string): void {
    this.selectedIdentityType = identityType;
  }

  get identityType(): typeof IdentityType {
    return IdentityType;
  }

  onDocumentTypeClear(): void {
    this.idDocumentFront = null;
    this.idDocumentBack = null;
    this.addressDocumentFront = null;
    this.addressDocumentBack = null;
    this.localKycDocument = [];
  }

  onDocumentFrontSelect(file: any): void {
    this.idDocumentFront = file;

    const find = this._findDocument(DocumentType.IDENTITY_PROOF, this.selectedIdentityType, DocumentSide.FRONT);
    if (!find) {
      this.localKycDocument.push({
        documentId: Guid.NewGuid(),
        documentRef: this._getDocumentRefId,
        type: DocumentType.IDENTITY_PROOF,
        identityType: this.selectedIdentityType,
        documentSide: DocumentSide.FRONT,
        file: file,
      });
    } else {
      const documents = this.localKycDocument.filter((doc) => doc.documentId !== find.documentId);
      documents.push({ ...find, file });
      this.localKycDocument = documents;
    }
  }

  onDocumentBackSelect(file: any): void {
    this.idDocumentBack = file;
    const find = this._findDocument(DocumentType.IDENTITY_PROOF, this.selectedIdentityType, DocumentSide.BACK);

    if (!find) {
      this.localKycDocument.push({
        documentId: Guid.NewGuid(),
        documentRef: this._getDocumentRefId,
        type: DocumentType.IDENTITY_PROOF,
        identityType: this.selectedIdentityType,
        documentSide: DocumentSide.BACK,
        file: file,
      });
    } else {
      const documents = this.localKycDocument.filter((doc) => doc.documentId !== find.documentId);
      documents.push({ ...find, file });
      this.localKycDocument = documents;
    }
  }

  onAddressFrontSelect(file: any): void {
    if (typeof FileReader !== 'undefined') {
      let reader = new FileReader();
      reader.onload = (e: any) => {
        this.addressPdfDocumentFront = e.target.result;
      };
      reader.readAsArrayBuffer(file);
    }

    this.addressDocumentFront = file;
    const find = this._findDocument(DocumentType.ADDRESS_PROOF, 'NONE', DocumentSide.FRONT);
    if (!find) {
      this.localKycDocument.push({
        documentId: Guid.NewGuid(),
        documentRef: this._getDocumentRefId,
        type: DocumentType.ADDRESS_PROOF,
        identityType: 'NONE',
        documentSide: DocumentSide.FRONT,
        file: file,
      });
    } else {
      const documents = this.localKycDocument.filter((doc) => doc.documentId !== find.documentId);
      documents.push({ ...find, file });
      this.localKycDocument = documents;
    }
  }

  readArrayBuffer(file: any): any {
    return new Promise((resolver, reject) => {
      file.arrayBuffer().then((buff: any) => {
        let x = new Uint8Array(buff); // x is your uInt8Array
        return resolver(x);
        // perform all required operations with x here.
      });
    });
  }

  onAddressBackSelect(file: any): void {
    this.addressDocumentBack = file;
    const find = this._findDocument(DocumentType.ADDRESS_PROOF, 'NONE', DocumentSide.BACK);
    if (!find) {
      this.localKycDocument.push({
        documentId: Guid.NewGuid(),
        documentRef: this._getDocumentRefId,
        type: DocumentType.ADDRESS_PROOF,
        documentSide: DocumentSide.BACK,
        identityType: 'NONE',
        file: file,
      });
    } else {
      const documents = this.localKycDocument.filter((doc) => doc.documentId !== find.documentId);
      documents.push({ ...find, file });
      this.localKycDocument = documents;
    }
  }

  public onBackClick(): void {
    if(this.isBrowser) {
      window.scroll({
        top: 70,
        left: 100,
        behavior: 'smooth',
      });
    }
    this.OnBack.next('basic-info');
  }

  uploadDocument(kycDocument?: KycDocument): void {
    if (!kycDocument) {
      kycDocument = this.localKycDocument[0];
    }
    this.accountStoreService.saveKycDocument(this.account.id, kycDocument);
  }

  submitDocument(): void {
    const documentRef = this._getDocumentRefId;
    this.accountStoreService.submitKycDocument(this.account.id, documentRef);
    if(this.isBrowser) {
      window.scroll({
        top: 70,
        left: 100,
        behavior: 'smooth',
      });
    }
    this.OnSubmit.emit('bank-account');
  }

  formContinue(): void {
    if(this.isBrowser) {
      window.scroll({
        top: 70,
        left: 100,
        behavior: 'smooth',
      });
    }
    this.OnSubmit.emit('bank-account');
  }

  get hasPendingDocument(): boolean {
    return !!this.kycDocuments.find((doc) => doc.status === 'PENDING');
  }

  private get _getDocumentRefId(): string {
    const documentRefId = this.kycDocuments.map((document) => document.documentRef)[0];
    if (documentRefId) {
      return documentRefId;
    } else {
      return this.documentRef;
    }
  }

  private _findDocument(type: DocumentType, identityType: string, documentSide: DocumentSide): KycDocument {
    if (this.localKycDocument.length) {
      return this.localKycDocument.find(
        (doc) => doc.type === type && doc.identityType === identityType && doc.documentSide === documentSide
      );
    } else {
      return this.kycDocuments.find(
        (doc) => doc.type === type && doc.identityType === identityType && doc.documentSide === documentSide
      );
    }
  }
}
